/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      document.body.removeChild(textArea);
      return true;
    } catch {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

/**
 * Download content as file. A content that is ALREADY a Blob (the ZIP
 * bundles) is used as-is : re-wrapping it in a new Blob would be
 * byte-identical but doubles peak memory, and the bundles run up to
 * the 50MB zipHelper ceiling. Consequence : mimeType applies only to
 * non-Blob content ; a Blob's OWN type wins, so callers passing a
 * Blob should omit the argument.
 */
export const downloadFile = (content, filename, mimeType) => {
  // Self-reporting footgun guard (dev builds only) : the signature
  // accepts a mimeType it must ignore for Blob content, so a caller
  // writing downloadFile(blob, "x.json", "application/json") would
  // otherwise silently ship the Blob's own type instead. mimeType is
  // deliberately defaulted INSIDE the body so an explicitly-passed
  // argument is distinguishable from the default.
  if (
    process.env.NODE_ENV !== "production" &&
    content instanceof Blob &&
    mimeType !== undefined
  ) {
    console.warn(
      "DOWNLOADFILE MIMETYPE ARGUMENT IS IGNORED FOR BLOB CONTENT : THE BLOB'S OWN TYPE WINS"
    );
  }
  const blob =
    content instanceof Blob
      ? content
      : new Blob([content], { type: mimeType ?? "text/plain" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // DEFERRED revoke : WebKit (Safari, and every iPhone/iPad browser,
  // which are all WebKit by platform requirement) does not guarantee
  // it has begun reading the blob URL when click() returns, so a
  // synchronous revoke can invalidate the URL before the download
  // starts - an intermittent, silent empty-download failure. Chrome
  // and Firefox capture the data at click time, making the deferral a
  // no-op for them. 100ms matches the deferral the PasswordGenerator
  // page's pre-consolidation hand-rolled download shipped in
  // production (git history : its setTimeout revoke predates the
  // shared-helper migration), rather than a guessed smaller value.
  // Cost : the Blob lives ~100ms longer, immaterial even at
  // zipHelper's 50MB ceiling.
  setTimeout(() => window.URL.revokeObjectURL(url), 100);
};

/**
 * Download base64 content as file
 */
export const downloadBase64File = (
  base64Content,
  filename,
  mimeType = "application/octet-stream"
) => {
  // Normalize: accept data URLs or raw base64; strip whitespace/newlines
  let b64 =
    typeof base64Content === "string"
      ? base64Content
      : String(base64Content || "");
  b64 = b64.trim();
  const dataUrlMatch = b64.match(/^data:.*;base64,(.*)$/);
  if (dataUrlMatch) {
    b64 = dataUrlMatch[1];
  }
  b64 = b64.replace(/\r?\n|\s/g, "");

  // Primary: Blob approach (avoids CSP issues with data: URLs). The
  // anchor mechanics live in downloadFile - this was the last
  // duplicate of that pattern after the 2026-08-07 consistency pass
  // consolidated every other site. The Blob is built HERE (with the
  // decoded bytes and the caller's mimeType) and passes through
  // downloadFile by identity ; only the data-URL fallback below keeps
  // its own anchor, because it has no Blob to hand over.
  try {
    const byteCharacters = atob(b64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++)
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    downloadFile(
      new Blob([byteArray], { type: mimeType }),
      filename || "download"
    );
    return;
  } catch (_) {
    // Fallback: data URL
  }

  try {
    const link = document.createElement("a");
    link.href = `data:${mimeType};base64,${b64}`;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (_) {
    // Final fallback: no-op to avoid crashing UI
  }
};

/**
 * Format date
 */
export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString();
};

/**
 * Get password strength
 */
export const getPasswordStrength = (password) => {
  if (!password) return { score: 0, text: "No password", color: "gray" };

  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[@$!%*?&]/.test(password)) score++;

  const strength = [
    { score: 0, text: "Very weak", color: "red" },
    { score: 1, text: "Weak", color: "orange" },
    { score: 3, text: "Fair", color: "yellow" },
    { score: 5, text: "Strong", color: "green" },
    { score: 6, text: "Very strong", color: "green" },
  ];

  return (
    strength.find((s) => score <= s.score) || strength[strength.length - 1]
  );
};
